import {Subject, from, EMPTY} from 'rxjs';
import {flatMap, map, startWith, merge} from 'rxjs/operators';
import {fromEventPattern} from 'rxjs';

const keys = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/**
 * Convert a MIDI note number to a note object
 * @param {number} number - The MIDI note number
 * @returns {Object} The note object
 */
export const numberToNote = number => ({
	key: keys[number % 12],
	octave: parseInt((number - number % 12) / 12, 10) - 1,
	number
});

/**
 * Convert a note object to a MIDI note number
 * @param {Object} note - The note object
 * @returns {number} The MIDI note number
 */
export const noteToNumber = note => (
	keys.indexOf(note.replace(/[0-9]+/, '')) +
	(parseInt(note.replace(/[A-Z#b]+/, ''), 10) + 1) * 12
);

/**
 * Parse a MIDI message
 * @param {Object} event - The MIDI message event
 * @returns {Object} The parsed MIDI message
 */
export const parseMidiMsg = event => {
	// Mask off the lower nibble (MIDI channel, which we don't care about)

	const status = event.data[0] & 0xf0;
	const binary = event.data[0].toString(2);
	const channel = event.data[0] - status + 1;
	let msg = {};

	switch (binary.slice(0, 4)) {
		// noteoff
		case "1000":
			msg = {
				state: 'noteOff',
				note: numberToNote(event.data[1])
			};
			break;
		// noteon
		case "1001":
			msg = (event.data[2] !== 0) // if velocity != 0, this is a note-on message
				? {
					state: 'noteOn',
					note: numberToNote(event.data[1]),
					velocity: parseFloat((event.data[2] / 127).toFixed(2))
				}
				: { // if velocity == 0, fall thru: it's a note-off.	MIDI's weird, ya'll.
					state: 'noteOff',
					note: numberToNote(event.data[1])
				};
			break;
		// pitch wheel
		case "1110":
			msg = {
				state: 'pitchBend',
				pitchValue: (event.data[2] === 64) ? 0 : parseFloat((event.data[2] / 63.5 - 1).toFixed(2))
			};
			break;
		// controller
		case "1011":
			msg = {
				state: "controller",
				controller: event.data[1],
				value: parseFloat((event.data[2] / 127).toFixed(2))
			};
			break;
		case "1100":
			msg = {
				state: "bankSelect",
				bank: event.data[1]
			};
			break;
		default:
			msg = {
				state: false
			};
			break;
	}

	return Object.assign({}, msg, {
		binary,
		status,
		channel,
		data: event.data
	});
};
//
// const hookUpMIDIInput = midiAccess => {
// 	var haveAtLeastOneDevice = false;
// 	var inputs = midiAccess.inputs.values();
// 	for (var input = inputs.next(); input && !input.done; input = inputs.next()) {
// 		input.value.onmidimessage = MIDIMessageEventHandler;
// 		haveAtLeastOneDevice = true;
// 	}
// };
//
// const onMIDIInit = midi => {
// 	hookUpMIDIInput(midi);
// 	midi.onstatechange = hookUpMIDIInput;
// };
//
// const onMIDIReject = err =>
// 	console.log(err, 'The MIDI system failed to start.');
//
// (navigator.requestMIDIAccess)
// 		&& navigator.requestMIDIAccess().then(onMIDIInit, onMIDIReject);

const parseAccess = access => {
	let inputs = [];
	let outputs = [];

	// console.log(access);

	access.inputs.forEach(input => inputs.push(input));
	access.outputs.forEach(output => outputs.push(output));
	return {access, inputs, outputs};
};

/**
 * Initialize the MIDI system
 * @returns {Object} The MIDI system object
 */
export const init = () => {
	const devices$ = new Subject();
	
	if (!navigator.requestMIDIAccess) {
		console.warn('Web MIDI API is not supported in this browser');
		return {
			devices$,
			msg$: new Subject()
		};
	}
	
	from(navigator.requestMIDIAccess()).pipe(
		flatMap(access => new (require('rxjs').Observable)(observer => {
			access.onstatechange = connection => observer.next(connection.currentTarget);
			return () => { access.onstatechange = null; };
		}).pipe(
			startWith(access)
		)),
		map(parseAccess)
		// map(data => (console.log('midi access', data), data))
	).subscribe({
		next: device => devices$.next(device),
		error: err => {
			console.error('Failed to request MIDI access:', err);
			// MIDI access was denied or failed - user can retry from UI
		}
	});
		// share()

	const msg$ = new Subject();
	devices$.pipe(
		flatMap(({access, inputs}) => inputs.reduce(
			(msgStream, input) => msgStream.pipe(
				merge(fromEventPattern(
					h => { input.onmidimessage = h; },
					() => { input.onmidimessage = null; }
				).pipe(
					map(msg => ({access, input, msg}))
				))
			),
			EMPTY
		))
	).subscribe(msg => msg$.next(msg));

	return {
		devices$,
		msg$
	};
};

export default{init, numberToNote, noteToNumber, parseMidiMsg};
